"use client";

import React, { createRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Radio from "../ui/radio";
import Input from "../ui/input";
import Link from "next/link";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useWindowSize } from "@/hooks/use-window-size";
import {
  ServiceCategories,
  useAssessmentStore,
} from "@/app/(withNavbarOnly)/care-assessment/v1/assessment-store";
import ReCAPTCHA from "react-google-recaptcha";
import { useSearchParams } from "next/navigation";
import PrimaryButton from "../ui/primary-button";
import { assessmentFormInputStyles } from "@/app/(withNavbarOnly)/care-assessment/v1/assessment-dynamic";
import LINKS from "@/lib/links";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";

export const sharedFormSchema = z.object({
  serviceCategory: z
    .string()
    .min(1, { message: "Service category is required" }),
  firstName: z.string().min(1, { message: "First name is required" }).max(32),
  lastName: z.string().min(1, { message: "Last name is required" }).max(32),
  email: z.string().min(1, { message: "Email is required" }).email(),
  phoneNumber: z
    .string()
    .min(1, { message: "Phone number is required" })
    .max(32),
  zipCode: z.string().min(1, { message: "Zip code is required" }).max(32),
});

interface SharedFormInputs {
  serviceCategory: ServiceCategories;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
}

interface SharedFormProps {
  handleNextButtonClick: () => void;
  disabledButton: boolean;
  buttonText: string;
  router: AppRouterInstance;
}

const SharedForm: React.FC<SharedFormProps> = ({
  handleNextButtonClick,
  disabledButton,
  buttonText,
  router,
}) => {
  const searchParams = useSearchParams();
  const serviceCategoryParams = searchParams.get("category");

  const size = useWindowSize();
  const recaptchaRef = createRef<ReCAPTCHA>();
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY;
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const handleRecaptchaChange = setRecaptchaToken;
  const makeStep1Invalid = useAssessmentStore(
    (state) => state.makeStep1Invalid
  );
  const makeStep1Valid = useAssessmentStore((state) => state.makeStep1Valid);
  const serviceCategory = useAssessmentStore((state) => state.serviceCategory);
  const changeStep1Values = useAssessmentStore(
    (state) => state.changeStep1Values
  );
  const submittedStep1Values = useAssessmentStore(
    (state) => state.submittedStep1Values
  );
  const setServiceCategory = useAssessmentStore(
    (state) => state.setServiceCategory
  );

  const {
    register,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<SharedFormInputs>({
    mode: "all",
    resolver: zodResolver(sharedFormSchema),
    defaultValues: {
      ...submittedStep1Values,
      serviceCategory:
        serviceCategoryParams &&
        (serviceCategoryParams === "services-for-families" ||
          serviceCategoryParams === "medical-staffing")
          ? serviceCategoryParams
          : serviceCategory,
    },
  });

  // We do this because the submit button is at the parent component
  // (src/app/assessment/page.tsx) and they communicate using zustand state
  const values = watch();
  useEffect(() => {
    setServiceCategory(values.serviceCategory);
    if (!isValid || recaptchaToken === null) {
      makeStep1Invalid();
      return;
    }
    makeStep1Valid();
    changeStep1Values(values);
    return;
  }, [
    values,
    isValid,
    makeStep1Valid,
    changeStep1Values,
    makeStep1Invalid,
    setServiceCategory,
    recaptchaToken,
  ]);

  return (
    <form
      aria-label="shared form"
      className="flex max-w-[358px] flex-1 flex-col gap-4 sm:max-w-[458px] lg:max-w-[520px]"
    >
      <div
        aria-label="service category radio group"
        className="flex flex-col gap-2 lg:grid lg:grid-cols-3 lg:gap-4"
      >
        <label className="font-roboto text-body-1-400 lg:col-span-1 lg:text-right">
          Service Category
        </label>
        <Radio
          required
          className="flex flex-row justify-between md:col-span-2 [&_div]:flex-1 lg:[&_div]:flex-none"
          labelClassName="font-roboto"
          orientation="horizontal"
          aria-label="service category"
          size="sm"
          value={watch("serviceCategory")}
          // Instead of refactoring this component to be compatible with
          // rhf's structure, let's just sense the changes instead.
          onValueChange={(v) => {
            if (serviceCategoryParams)
              router.replace(LINKS.careAssessment.href);
            setValue("serviceCategory", v as ServiceCategories, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
          }}
          items={[
            {
              id: "services-for-families",
              label: "Services for Families",
              value: "services-for-families",
            },
            {
              id: "medical-staffing",
              label: "Medical Staffing",
              value: "medical-staffing",
            },
          ]}
        />
      </div>
      <Input
        aria-label="First Name"
        label="First Name"
        autoComplete="given-name"
        placeholder="First Name"
        error={!!errors.firstName}
        errorMessage={errors.firstName?.message}
        className={assessmentFormInputStyles}
        {...register("firstName")}
      />
      <Input
        aria-label="Last Name"
        label="Last Name"
        autoComplete="family-name"
        placeholder="Last Name"
        error={!!errors.lastName}
        errorMessage={errors.lastName?.message}
        className={assessmentFormInputStyles}
        {...register("lastName")}
      />
      <Input
        aria-label="Email Address"
        label="Email Address"
        type="email"
        autoComplete="email"
        error={!!errors.email}
        errorMessage={errors.email?.message}
        placeholder="Email Address"
        className={assessmentFormInputStyles}
        {...register("email")}
      />
      <Input
        aria-label="Phone Number"
        label="Phone Number"
        placeholder="Phone Number"
        error={!!errors.phoneNumber}
        errorMessage={errors.phoneNumber?.message}
        className={assessmentFormInputStyles}
        {...register("phoneNumber")}
      />
      <Input
        aria-label="ZIP Code where the service is needed"
        label={
          size === "lg" ? "ZIP Code" : "ZIP Code where the service is needed"
        }
        placeholder="ZIP Code where the service is needed"
        error={!!errors.zipCode}
        errorMessage={errors.zipCode?.message}
        className={assessmentFormInputStyles}
        {...register("zipCode")}
      />
      <div className="lg:grid lg:grid-cols-3 lg:gap-4 lg:[&_div]:col-span-2">
        <p
          aria-label="disclaimer"
          className="mt-2 font-roboto text-neutral-600 lg:col-span-2 lg:col-start-2 lg:mt-0"
        >
          By submitting this form, you agree to Care Indeed&apos;s{" "}
          <Link
            className="underline"
            href={LINKS.termsOfUse.href}
            prefetch={!LINKS.termsOfUse.doNotPrefetch}
          >
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link
            className="underline"
            href={LINKS.privacyPolicy.href}
            prefetch={!LINKS.privacyPolicy.doNotPrefetch}
          >
            Privacy Policy
          </Link>
          .
        </p>
      </div>
      {recaptchaKey && (
        <div
          className="lg:grid lg:grid-cols-3"
          data-testid="recap container"
        >
          <ReCAPTCHA
            className="lg:col-span-2 lg:col-start-2"
            ref={recaptchaRef}
            sitekey={recaptchaKey}
            onChange={handleRecaptchaChange}
          />
        </div>
      )}
      <PrimaryButton
        id="assessment-form-button"
        size="md"
        color="orange"
        onClick={handleNextButtonClick}
        aria-label="next button"
        disabled={disabledButton}
        className="mt-7"
        type="button"
      >
        {buttonText}
      </PrimaryButton>
    </form>
  );
};

export default SharedForm;
